import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND } from "../data/content";

const links = [
  { to: "/programs", label: "Capabilities" },
  { to: "/how-we-design", label: "Method" },
  { to: "/who-we-work-with", label: "Work" },
  { to: "/insights", label: "Research" },
  { to: "/about", label: "About" }
];

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[color:var(--paper)] border-b border-[color:var(--line-soft)]"
            : "bg-[color:var(--paper)] border-b border-transparent"
        }`}
      >
        <div className="wrap flex items-center justify-between h-[76px]">
          <Link
            to="/"
            data-testid="nav-wordmark"
            className="flex flex-col leading-tight"
          >
            <span
              className="font-hook-black text-[#0E1330] relative inline-block"
              style={{ fontSize: 26, letterSpacing: "-0.015em", lineHeight: 1 }}
            >
              <span style={{ display: "inline-block", position: "relative" }}>
                art<span style={{ position: "relative" }}>i<span aria-hidden="true" style={{ position: "absolute", top: "-0.45em", left: "50%", transform: "translateX(-50%)", width: 7, height: 7, borderRadius: 999, background: "#F9D70A", display: "block" }} /></span>culex
              </span>
            </span>
            <span
              className="hidden md:inline stamp-dim"
              style={{ fontSize: 10, letterSpacing: "0.16em", marginTop: 4 }}
            >
              Capability development partner
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${slugify(l.label)}`}
                className={({ isActive }) =>
                  `stamp transition-colors ${
                    isActive ? "text-[#2B2BD4]" : "text-[#0E1330] hover:text-[#EF478A]"
                  }`
                }
                style={{ fontSize: 13 }}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-tell-us"
              className="btn-ink"
              style={{ padding: "12px 22px", fontSize: 12 }}
            >
              Start a conversation
            </Link>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            aria-label="Open menu"
            onClick={() => setOpen(true)}
            className="lg:hidden text-[#0E1330] p-2"
          >
            <Menu size={26} />
          </button>
        </div>
      </header>

      {open && (
        <div
          data-testid="mobile-menu"
          className="fixed inset-0 z-[60] bg-[color:var(--paper)] flex flex-col"
        >
          <div className="wrap flex items-center justify-between h-[76px]">
            <span className="font-hook-black text-[#0E1330] relative inline-block" style={{ fontSize: 26, letterSpacing: "-0.015em" }}>
              art<span style={{ position: "relative" }}>i<span aria-hidden="true" style={{ position: "absolute", top: "-0.45em", left: "50%", transform: "translateX(-50%)", width: 7, height: 7, borderRadius: 999, background: "#F9D70A" }} /></span>culex
            </span>
            <button
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-close"
              className="text-[#0E1330] p-2"
              aria-label="Close menu"
            >
              <X size={28} />
            </button>
          </div>
          <div className="wrap flex-1 flex flex-col gap-4 pt-8">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${slugify(l.label)}`}
                className={({ isActive }) =>
                  `font-hook text-[34px] leading-none pb-3 border-b border-[color:var(--line-soft)] ${
                    isActive ? "text-[#2B2BD4]" : "text-[#0E1330]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/contact" className="btn-ink self-start mt-4" data-testid="mobile-nav-cta">
              Start a conversation
            </Link>
            <span className="stamp-dim mt-2" style={{ fontSize: 11 }}>Founder, Articulex</span>
          </div>
        </div>
      )}
    </>
  );
}
